import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Leaf, Sprout, Truck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import { CategoryGridSkeleton } from '../components/Skeleton';
import ErrorState from '../components/ErrorState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as catalogApi from '../api/endpoints/catalog';
import { isStaleLocationError } from '../utils/staleLocation';
import { visibleCategories } from '../utils/categories';
import { resolveCategoryVisual } from '../config/categoryVisuals';
import { focusRing, homeRail } from '../styles/ui';

// Brand copy already used across the site — no new claims.
const HERO_POINTS = [
  { icon: Leaf, title: 'Farm Fresh', subtitle: 'Direct from farms' },
  { icon: BadgeCheck, title: 'Quality Checked', subtitle: 'Hygienic & safe' },
  { icon: Truck, title: 'Same-Day Delivery', subtitle: 'At your doorstep' },
  { icon: Sprout, title: 'Wide Variety', subtitle: 'Everything you need' },
];

// One category from GET /categories, in the approved design's card: photo,
// name, the backend's own product count and an arrow. A category the backend
// marks empty is shown with its icon and "Coming soon" and is not a link.
const CategoryTile = ({ category }) => {
  const { image, Icon, tint } = resolveCategoryVisual(category.key, category.iconHint);
  const body = (
    <>
      <span className={`block h-[160px] overflow-hidden bg-gradient-to-br ${tint}`}>
        {image ? (
          <img src={image} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <span className="relative w-full h-full flex items-center justify-center bg-[#EAF6E6]">
            <DecorLeaf className="absolute right-6 top-4 w-8 text-emerald-300/80 rotate-[24deg]" />
            <DecorLeaf className="absolute left-6 bottom-4 w-6 text-emerald-300/70 -rotate-[20deg]" />
            <span className="w-[72px] h-[72px] rounded-full bg-[#DFF3DA] text-emerald-700 flex items-center justify-center">
              <Icon className="w-8 h-8" strokeWidth={1.8} aria-hidden="true" />
            </span>
          </span>
        )}
      </span>
      <span className="flex items-center justify-between gap-3 px-4 py-[14px]">
        <span className="min-w-0">
          <span className="block text-[16px] font-bold text-emerald-950 leading-tight truncate">{category.label}</span>
          <span className="block text-[13px] text-ink-secondary mt-[3px]">
            {category.isEmpty ? 'Coming soon' : `${category.count} product${category.count === 1 ? '' : 's'}`}
          </span>
        </span>
        <span className="w-10 h-10 rounded-full bg-[#E4F3E5] text-emerald-800 flex items-center justify-center shrink-0 group-hover:bg-emerald-100 transition-colors" aria-hidden="true">
          <ArrowRight size={18} strokeWidth={2.2} />
        </span>
      </span>
    </>
  );
  const shell = 'group block bg-white rounded-2xl border border-[#E6EEE6] shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)] overflow-hidden';
  if (category.isEmpty) return <div className={`${shell} opacity-80`}>{body}</div>;
  return (
    <Link to={`/categories/${category.key}`} className={`${shell} hover:shadow-card transition-shadow ${focusRing}`}>
      {body}
    </Link>
  );
};

const CategoryListingPage = () => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const requestIdRef = useRef(0);

  usePageMetadata({
    title: 'Shop by Category | Fresh & Homely',
    description: 'Browse fresh meat, seafood, dairy and grocery categories at Fresh & Homely.',
    canonicalPath: '/categories',
  });

  const load = () => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', data: null, error: null });
    catalogApi
      .listCategories()
      .then((response) => {
        if (requestId === requestIdRef.current) {
          setState({ status: 'success', data: visibleCategories(response.data), error: null });
        }
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current || isStaleLocationError(err)) return;
        setState({ status: 'error', data: null, error: err.message });
      });
  };

  useEffect(() => {
    load();
    return () => {
      requestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Navbar />
      <main className="bg-white">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F3FAF1_0%,#F7FBF5_70%,#FFFFFF_100%)]">
          <div className="pointer-events-none select-none absolute inset-x-0 top-20 bottom-0" aria-hidden="true">
            <img src="/pages/categories-hero-leaves.webp" alt="" width="260" height="277" className="hidden xl:block absolute left-0 top-0 h-full w-auto" />
            <img src="/pages/categories-hero-basket.webp" alt="" width="396" height="277" className="hidden lg:block absolute right-0 top-0 h-full w-auto" />
          </div>
          <div className={`${homeRail} relative`}>
            <div className="text-center pt-[28px] pb-[24px] xl:min-h-[247px]">
              <span className="inline-flex items-center gap-1.5 h-[30px] px-4 rounded-full bg-white border border-emerald-100 text-emerald-800 text-[10.5px] font-bold tracking-[0.12em] uppercase shadow-[0_1px_3px_rgba(15,51,24,0.08)]">
                <DecorLeaf className="w-3.5 text-emerald-600" />
                Fresh • Hygienic • Handpicked
              </span>
              <h1 className="serif mt-[14px] text-[34px] sm:text-[40px] xl:text-[46px] font-bold text-emerald-950 leading-[1.05]">
                Shop by Category
              </h1>
              <p className="mt-[10px] text-[15px] xl:text-[17px] text-[#4B5563]">
                Explore our full range of handpicked fresh products.
              </p>
              <ul className="mt-[26px] flex flex-wrap justify-center gap-x-10 xl:gap-x-14 gap-y-4">
                {HERO_POINTS.map(({ icon: Icon, title, subtitle }) => (
                  <li key={title} className="flex items-center gap-3 text-left">
                    <span className="w-[46px] h-[46px] rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                      <Icon size={21} strokeWidth={1.9} aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-[13.5px] font-bold text-emerald-950 leading-tight">{title}</span>
                      <span className="block text-[12px] text-ink-secondary mt-[3px]">{subtitle}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Grid ─────────────────────────────────────────────────────── */}
        <section className="pt-[30px] pb-14">
          <div className={homeRail}>
            <div className="xl:px-[92px]">
              {state.status === 'loading' && <CategoryGridSkeleton count={8} />}
              {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
              {state.status === 'success' && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="category-grid">
                  {state.data.map((category) => (
                    <li key={category.key}>
                      <CategoryTile category={category} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CategoryListingPage;
